import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { CartItemAdditionTypes } from '../../constants/event';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  cartActions,
  setSequenceId,
  setCartItemCollapseState,
} from '../../reducers/cartSlice';
import {
  dialogActions,
  selectItemAndResetNavigation,
  resetDialogSelectedItem,
} from '../../reducers/dialogSlice';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import {
  CartItem,
  getAutoCombinedCartItemIdsCount,
  shouldCollapse,
} from '../../utils/cart';
import Colors from '../../utils/color';
import CartItemModGroups from './CartItemModGroups';
import OrderProgressBar from './OrderProgressBar';
import { upsell } from '../../utils/upsell';

import {
  addRefreshNonComboItem,
  cloneDeleteMUComboItem,
  refreshMUAutoCombinedItems,
  resetCombinedCartItemds,
} from '../../redux/features/autoCombo/autoCombo.slice';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';
import { useAppSelector } from '../../app/hooks';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import { selectMenuModuleCartErrors } from '../../selectors/order';
import {
  cartErrorMatchesItem,
  getModifierErrorPaths,
} from '../../utils/menuModuleError';
import { ItemCartActionButtons } from '../../constants/enums';
import useMenuUnificationOrderHandlers from '../../hooks/menuUnification/useMenuUnificationOrderHandler.hooks';
import {
  selectCanCopyToDraft,
  selectIsAgentActionBlocked,
} from '../../redux/features/agentMode/agentMode.selector';
import { BLOCKED_AFFORDANCE_STYLE } from '../../utils/agentModeAffordance';
import { isAutoCombinedLine } from '../../redux/features/draftCart/draftCart.thunks';
import CopyToDraftControl, { COPY_ANCHOR_STYLE } from './CopyToDraftControl';
interface ICartItemReview {
  cartItem: CartItem;
  quantity: number;
  status?: OrderStatus;
  collapseState: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  content: {
    padding: `${theme.spacing(1.5)} !important`,
    '& > div.cartitem-mod-group': {
      padding: '0 !important',
    },
  },
  mainItemname: {
    lineHeight: '1.57 !important',
    fontSize: `${theme.spacing(1.5)} !important`,
    fontWeight: 'bold !important',
  },
  lineItemsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '24px',
  },
  itemPrice: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.75rem !important',
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      cursor: 'pointer',
    },
  },
  itemActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    '& .MuiButtonBase-root.MuiButton-root': {
      textTransform: 'none',
      color: Colors.black,
      fontWeight: 'bold',
      flexDirection: 'row-reverse',
      textAlign: 'right',
      padding: '0px',
    },
  },
  selectQuantity: {
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '&:before': {
      borderBottom: 'none !important',
    },
    '& .MuiSelect-select': {
      textAlign: 'center',
      minWidth: '40px !important',
      paddingRight: '2px !important',
      paddingLeft: '0px !important',
    },
  },
  autoCombinedText: {
    paddingBottom: theme.spacing(1),
  },
  // ---- hitl_3_0 item-row refinements (gated; legacy is untouched) ----
  // Tighter card gap + content padding so more items fit without scrolling.
  cardHitl3: {
    marginTop: `${theme.spacing(1)} !important`,
  },
  contentHitl3: {
    padding: `${theme.spacing(1)} !important`,
    paddingBottom: `${theme.spacing(0.75)} !important`,
  },
  actionsWrapperHitl3: {
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  // Quantity stepper as one segmented control: − | count | +.
  stepperHitl3: {
    display: 'inline-flex',
    alignItems: 'stretch',
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(0.75),
    overflow: 'hidden',
    '& .MuiIconButton-root': {
      borderRadius: 0,
      padding: theme.spacing(0.25, 0.5),
      color: Colors.daintree,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: Colors.silverChalice,
    },
  },
  selectQuantityHitl3: {
    borderLeft: `1px solid ${Colors.alto}`,
    borderRight: `1px solid ${Colors.alto}`,
    // Center the quantity in its cell. The base selectQuantity uses text-align,
    // which a flex box ignores — so it needs justifyContent, plus symmetric
    // padding (the base sets asymmetric l/r padding) to sit dead center.
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '34px !important',
      padding: '0 4px !important',
    },
  },
  // Icon-only Duplicate / Remove (tooltip + aria-label supply the text). Remove
  // carries the destructive red.
  iconActionHitl3: {
    padding: `${theme.spacing(0.5)} !important`,
    color: `${Colors.daintree} !important`,
  },
  iconActionDangerHitl3: {
    padding: `${theme.spacing(0.5)} !important`,
    color: `${Colors.guardsmanRed} !important`,
  },
}));

export const CartItemReview = (props: ICartItemReview) => {
  const { cartItem, quantity, status, collapseState } = props;
  const cartItemRef = useRef<HTMLDivElement>(null);
  const actionableElementsRef = useRef<any[]>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isUnifiedMenu,
    isPRPAutoComboEnabled,
    isBMAutoComboEnabled,
    autoCombinedCartItemsQuantity,
  } = useAutoCombo();
  const { sequenceId, cartItems } = useSelector(
    (state: RootState) => state.cart
  );
  const { selectedItemCartId } = useSelector(
    (state: RootState) => state.dialog
  );

  const { sendAgentInterception } = useAgentInterception();
  const quantityList: number[] = new Array(19).fill(0);

  const isPRPAutoCombined = !!(
    isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds?.length
  );
  const isMUAutoCombined = !!(
    isUnifiedMenu &&
    isBMAutoComboEnabled &&
    cartItem.isAutoCombined
  );
  const isCartItemAutoCombined = isPRPAutoCombined || isMUAutoCombined;
  const cartItemsQuantity = useAppSelector(selectCartItemsQuantity);
  // HITL-1606: outside Intercept the agent may look but not act. UX only —
  // useAgentInterception refuses the emit regardless.
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  // HITL-1614: `Shadow` only. In `Intercept` these lines already ARE the order,
  // so there is nothing to copy into a draft. An auto-combined line is excluded
  // because it is a combo assembled from other cart lines — its
  // `autoCombinedCartItemIds` address `cartSlice`'s id space and mean nothing in
  // the draft. Hypothesis lines never reach here at all; `OrderReview` renders
  // those through `HypothesisItemReview`.
  const canCopyToDraft = useAppSelector(selectCanCopyToDraft);
  // hitl_3_0 only: refined item row (segmented stepper + icon-only actions +
  // tighter padding). Legacy keeps the text-button layout untouched.
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const menuModuleCartErrors = useAppSelector(selectMenuModuleCartErrors);

  const handleCollapseStateChange = useCallback(() => {
    dispatch(setCartItemCollapseState({ cartItemId: cartItem.cartItemId }));
  }, [dispatch]);

  const sendAgentInterventionEvent = (description: string) => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description,
      },
    });
  };

  const {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  } = useMenuUnificationOrderHandlers();

  const cloneDeleteAutoCombinedCartItems = (operator: string) => {
    if (isMUAutoCombined) {
      dispatch(
        cloneDeleteMUComboItem({
          cartItemId: cartItem.cartItemId.toString(),
          operator,
          sequenceId,
        })
      );
      if (operator === '+') {
        dispatch(setSequenceId(sequenceId + 1));
      } else if (
        operator === '-' &&
        Object.keys(autoCombinedCartItemsQuantity).length === 1
      ) {
        // Only 1 item and it is being deleted -- so clear the entire cart
        dispatch(resetDialogSelectedItem());
        dispatch(cartActions.clearCart());
        dispatch(resetCombinedCartItemds());
      }
      dispatch(
        refreshMUAutoCombinedItems({
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );
    }
  };

  const cloneCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('+');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] +
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      const cartItemIdString = cartItem.cartItemId.toString();

      // The quantity shown to user is used for cloning the item --- as for brand-menu non-combined item the quantity may not match
      dispatch(
        cartActions.cloneCartItem({
          id: cartItemIdString,
          quantity,
        })
      );
      dispatch(dialogActions.cloneSelectedItem({ cartItemId: sequenceId + 1 }));
    }

    sendAgentInterventionEvent(`Clone ${cartItem.name} in the same order`);
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };

  const deleteCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('-');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] -
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      dispatch(cartActions.deleteCartItem(cartItem.cartItemId.toString()));
      if (selectedItemCartId === cartItem.cartItemId) {
        const cartItemIds = Object.keys(cartItems)
          .filter((id) => id !== selectedItemCartId.toString())
          .map((id) => parseInt(id));
        const newCartItemId = Math.max(...cartItemIds);
        if (newCartItemId > 0) {
          dispatch(
            dialogActions.selectItemAndResetNavigation({
              item: cartItems[newCartItemId.toString()],
              itemCartId: newCartItemId,
            })
          );
        } else {
          dispatch(dialogActions.selectItemAndResetNavigation(undefined));
        }
      }
    }
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
    sendAgentInterventionEvent(`${cartItem.name} is removed from order`);
  };

  const updateQuantity = (newQuantity: number) => {
    if (isCartItemAutoCombined) return; // not support for auto combined item

    const cartItemIdString = cartItem.cartItemId.toString();
    if (Object.keys(autoCombinedCartItemsQuantity).includes(cartItemIdString)) {
      // Update the quantity in non-combined autoComboItemQuantity
      dispatch(
        addRefreshNonComboItem({
          cartItemId: cartItemIdString,
          cartItem,
          quantity: newQuantity,
        })
      );
    }

    dispatch(
      cartActions.updateQuantity({
        cartItemId: cartItemIdString,
        quantity: newQuantity,
      })
    );
    sendAgentInterventionEvent(
      `Quantity of ${cartItem.name} changed to ${newQuantity}`
    );
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      updateQuantity(quantity - 1);
    }
  };

  const handleIncreaseQuantity = () => {
    if (quantity < 19) {
      updateQuantity(quantity + 1);
    }
  };

  const onCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    const isSelectDropdownClick =
      target.closest('.MuiMenuItem-root') ||
      target.closest('.MuiMenu-root') ||
      target.closest('.MuiPaper-root[role="presentation"]');

    const isActionableClick =
      actionableElementsRef.current.some(
        (element) => element && element.contains(target)
      ) || isSelectDropdownClick;

    // Collapse FIRST, and outside the gate below: it is display state on a line
    // the agent is reading, not an action on the order, and an agent shadowing a
    // twelve-line order needs to be able to fold the ones they have checked.
    if (!isActionableClick) {
      handleCollapseStateChange();
    }

    if (isCartItemAutoCombined) return;
    // HITL-1649: outside `Intercept` this line does not get to claim the centre
    // pane. HITL-1617 routes the pane's writes by cart scope, so selecting a
    // Live order line points that write path back at `cartSlice` — the agent
    // edits the AI's order believing they are building their own. It also takes
    // the pane away from the My Cart line they had open, which is the surface
    // `Shadow` exists to give them.
    //
    // The same gate every other agent action on the order already reads
    // (HITL-1606), so with the flag off, and in `Intercept`, this is the
    // unchanged path.
    if (isAgentActionBlocked) return;
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItem.cartItemId,
      })
    );
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };
  const collapse = shouldCollapse(collapseState, status);
  // A menu-module error whose path is just this item (no modifier segments)
  // means the item itself is the failing node — highlight its name.
  const isItemLevelError = menuModuleCartErrors.some(
    (error) =>
      cartErrorMatchesItem(error, cartItem.cartItemId, cartItem.name) &&
      error.nodePath.length === 1
  );
  const itemColor = isItemLevelError ? Colors.Thunderbird : Colors.daintree;
  const itemCardBorder =
    selectedItemCartId === cartItem.cartItemId
      ? `3px solid ${itemColor}`
      : // hitl_3_0: unselected cards sit on a light background where the bare
      // elevation shadow leaves the side edges invisible, so give them a subtle
      // border. Legacy keeps the borderless (shadow-only) look.
      isHitl3
      ? `1px solid ${Colors.alto}`
      : 'unset';

  useEffect(() => {
    if (selectedItemCartId === cartItem.cartItemId) {
      cartItemRef.current?.scrollIntoView();
    }
  }, [selectedItemCartId]);

  const card = (
    <Card
      id="cart-item"
      className={
        isHitl3 ? `${classes.card} ${classes.cardHitl3}` : classes.card
      }
      elevation={3}
      sx={{
        borderRadius: 1.5,
        cursor: isCartItemAutoCombined ? 'default' : 'pointer',
        backgroundColor: `${
          cartItem.addedBy === CartItemAdditionTypes.AI
            ? Colors.aiItemIndicator
            : isCartItemAutoCombined
            ? Colors.gallery
            : Colors.white
        }`,
      }}
      onClick={onCardClick}
      ref={cartItemRef}
      style={{
        borderLeft: itemCardBorder,
        borderRight: itemCardBorder,
        borderTop: itemCardBorder,
      }}
    >
      <CardContent
        className={
          isHitl3
            ? `${classes.content} ${classes.contentHitl3}`
            : classes.content
        }
      >
        <div className={classes.lineItemsWrapper}>
          <Typography
            variant="subtitle2"
            className={classes.mainItemname}
            style={{ color: itemColor }}
          >
            {cartItem.name}
          </Typography>
          {collapse ? (
            <ExpandMoreIcon style={{ padding: 0 }}></ExpandMoreIcon>
          ) : (
            <ExpandLessIcon style={{ padding: 0 }}></ExpandLessIcon>
          )}
        </div>
        {collapse ? null : (
          <>
            {isCartItemAutoCombined && (
              <Typography
                variant="subtitle2"
                className={`${classes.mainItemname} ${classes.autoCombinedText}`}
                style={{ color: itemColor }}
              >
                (Auto-combined)
              </Typography>
            )}
            {cartItem && cartItem.childModifierGroups && (
              <CartItemModGroups
                cartItem={cartItem}
                modGroupCartItem={cartItem}
                quantity={quantity}
                // HITL-1649: the second way into the centre pane. A modifier
                // group row, and a child modifier row inside it, both call
                // `selectModGroupToFocus`, which dispatches the same selection
                // the card click does — so gating only the card would leave the
                // pane reachable one click deeper.
                selectable={!isCartItemAutoCombined && !isAgentActionBlocked}
                errorNodePaths={getModifierErrorPaths(
                  menuModuleCartErrors,
                  cartItem.cartItemId,
                  cartItem.name
                )}
              />
            )}
          </>
        )}

        {/* HITL-1613: the card's own `cursor: pointer` (set in `sx` above) is
            correct and deliberately left alone — clicking the card selects the
            line to VIEW it and toggles the collapse, which is the whole point
            of Shadow. It is inherited by this action row, though, where
            every control IS refused: MUI's disabled state sets
            `pointer-events: none`, so the cursor fell through to the card's
            pointer and the greyed-out stepper / Remove / Duplicate still
            invited the press. The row owns its own cursor while the mode is
            silent. Inline rather than a `makeStyles` rule so no class name in
            this sheet renumbers. */}
        <div
          className={
            isHitl3
              ? `${classes.actionsWrapper} ${classes.actionsWrapperHitl3}`
              : classes.actionsWrapper
          }
          style={isAgentActionBlocked ? BLOCKED_AFFORDANCE_STYLE : undefined}
        >
          <div className={isHitl3 ? classes.stepperHitl3 : undefined}>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.DECREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleDecreaseQuantity}
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              <RemoveIcon fontSize={isHitl3 ? 'small' : undefined} />
            </IconButton>
            <Select
              value={quantity}
              size="small"
              variant="standard"
              onChange={(e) => updateQuantity(e.target.value as number)}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 200 },
                },
              }}
              className={
                isHitl3
                  ? `${classes.selectQuantity} ${classes.selectQuantityHitl3}`
                  : classes.selectQuantity
              }
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              {quantityList.map((q, index) => (
                <MenuItem
                  key={`quantity-${index}`}
                  value={index + 1}
                  id={`quantity-${index}`}
                >
                  {index + 1}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.INCREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleIncreaseQuantity}
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              <AddIcon fontSize={isHitl3 ? 'small' : undefined} />
            </IconButton>
          </div>
          <div className={classes.itemActions}>
            {isHitl3 ? (
              // Icon-only under hitl_3_0 (tooltip + aria-label carry the label);
              // DOM order Remove→Duplicate + row-reverse renders Duplicate | Remove.
              <>
                <IconButton
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.REMOVE
                    ] = el)
                  }
                  className={classes.iconActionDangerHitl3}
                  size="small"
                  title="Remove"
                  aria-label="Remove"
                  disabled={isAgentActionBlocked}
                  onClick={deleteCartItem}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.DUPLICATE
                    ] = el)
                  }
                  className={classes.iconActionHitl3}
                  size="small"
                  title="Duplicate"
                  aria-label="Duplicate"
                  disabled={isAgentActionBlocked}
                  onClick={cloneCartItem}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.REMOVE
                    ] = el)
                  }
                  variant="text"
                  onClick={deleteCartItem}
                  size="small"
                  disabled={isAgentActionBlocked}
                >
                  Remove
                </Button>
                <Button
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.DUPLICATE
                    ] = el)
                  }
                  variant="text"
                  onClick={cloneCartItem}
                  size="small"
                  disabled={isAgentActionBlocked}
                >
                  Duplicate
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
      <OrderProgressBar status={status} cartItem={cartItem} />
    </Card>
  );

  // HITL-1614: the copy control needs a positioned ancestor to sit ON the card's
  // left border. Rather than adding `position: relative` to the Card's own `sx`
  // — which would change its emotion hash and churn every existing snapshot —
  // the anchor is an extra wrapper that exists ONLY when the control does. With
  // the flag off, or in any mode but `Shadow`, the tree below is exactly what it
  // has always been.
  // HITL-1644: the auto-combo exclusion moved to `isAutoCombinedLine` so this
  // gate and the copy thunks cannot drift — the cart-level copy has to skip
  // exactly the lines this control is not offered on. The predicate itself is
  // unchanged; its reasoning now lives beside it in `draftCart.thunks`.
  if (!canCopyToDraft || isAutoCombinedLine(cartItem)) return card;

  return (
    <div style={COPY_ANCHOR_STYLE}>
      <CopyToDraftControl cartItem={cartItem} />
      {card}
    </div>
  );
};
